/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-07-23
 */

// Add third-party dependencies.
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Alert,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton,
  Fade,
  Zoom,
  Backdrop,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  Error,
  Star,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Add local dependencies.
import {
  useSessionRegisterMutation,
  useEmailVerificationRequestMutation,
} from '../../../features/auth';
import TurnstileWidget from '../Waitlist/waitlist/TurnstileWidget';
import { useFormValidation } from '../../../hooks/useFormValidation';
import { registrationSchema } from '../../../utils/validationSchemas';
import {
  validatePasswordStrength,
  getPasswordStrengthScore,
  getPasswordStrengthLabel,
  getPasswordStrengthColor,
} from '../../../utils/passwordStrength';
import { accessibilityUtils, ARIA_ROLES } from '../../../utils/accessibility';
import { YouTubeVideoButton } from '../../common';

interface FormData {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
  termsAccepted: boolean;
  privacyAccepted: boolean;
  marketingEmails: boolean;
}

interface RegisterFormProps {
  prefilledEmail?: string;
  emailDisabled?: boolean;
  onRegistrationSuccess?: () => void;
}

/**
 * Registration form component for user sign-up.
 * @example
 * return <RegisterForm />
 * @returns {React.ReactElement} The registration form component.
 */
const RegisterForm: React.FC<RegisterFormProps> = ({
  prefilledEmail,
  emailDisabled = false,
  onRegistrationSuccess,
}) => {
  const { t, i18n } = useTranslation('auth');
  const navigate = useNavigate();
  const [register, { isLoading }] = useSessionRegisterMutation();
  const [emailVerificationRequest] = useEmailVerificationRequestMutation();

  const [formData, setFormData] = useState<FormData>({
    email: prefilledEmail || '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    termsAccepted: false,
    privacyAccepted: false,
    marketingEmails: false,
  });

  // Initialize validation hook
  const {
    errors,
    validateForm,
    handleInputChange: handleValidationChange,
  } = useFormValidation<FormData>(registrationSchema, { validateOnBlur: true });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password visibility handlers
  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword(!showConfirmPassword);
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const [apiError, setApiError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Enhanced error handling state for existing email detection
  const [showResendVerificationButton, setShowResendVerificationButton] =
    useState(false);
  const [showForgotPasswordLink, setShowForgotPasswordLink] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string>('');
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [fieldValidationStates, setFieldValidationStates] = useState<{
    email: 'idle' | 'valid' | 'invalid';
    firstName: 'idle' | 'valid' | 'invalid';
    lastName: 'idle' | 'valid' | 'invalid';
    phoneNumber: 'idle' | 'valid' | 'invalid';
    password: 'idle' | 'valid' | 'invalid';
    confirmPassword: 'idle' | 'valid' | 'invalid';
  }>({
    email: 'idle',
    firstName: 'idle',
    lastName: 'idle',
    phoneNumber: 'idle',
    password: 'idle',
    confirmPassword: 'idle',
  });

  // Plan selection state
  const [selectedPlan, setSelectedPlan] = useState<{
    selectedPlan: string;
    billingCycle: string;
    timestamp: number;
  } | null>(null);

  // Check for plan selection from sessionStorage on mount
  useEffect(() => {
    try {
      const storedPlan = sessionStorage.getItem('selectedPlan');
      if (storedPlan) {
        const planData = JSON.parse(storedPlan);
        // Check if plan data is recent (within 24 hours)
        const isRecent = Date.now() - planData.timestamp < 24 * 60 * 60 * 1000;
        if (isRecent) {
          setSelectedPlan(planData);
        } else {
          // Clear expired plan selection
          sessionStorage.removeItem('selectedPlan');
        }
      }
    } catch (error) {
      console.warn(
        'Failed to parse plan selection from sessionStorage:',
        error
      );
      sessionStorage.removeItem('selectedPlan');
    }
  }, []);

  /**
   * Get display name for plan
   */
  const getPlanDisplayName = (planSlug: string) => {
    switch (planSlug) {
      case 'free':
        return t('register.plan.free');
      case 'starter':
        return t('register.plan.starter');
      case 'growth':
        return t('register.plan.growth');
      case 'pro':
        return t('register.plan.pro');
      default:
        return t('register.plan.selected');
    }
  };

  /**
   * Get plan badge for special plans
   */
  const getPlanBadge = (planSlug: string) => {
    switch (planSlug) {
      case 'growth':
        return t('register.plan.badge.mostPopular');
      case 'pro':
        return t('register.plan.badge.premium');
      default:
        return null;
    }
  };

  // Generate unique IDs for accessibility
  const formId = accessibilityUtils.generateId('register-form');
  const emailErrorId = accessibilityUtils.generateId('email-error');
  const firstNameErrorId = accessibilityUtils.generateId('firstName-error');
  const lastNameErrorId = accessibilityUtils.generateId('lastName-error');
  const phoneErrorId = accessibilityUtils.generateId('phone-error');
  const passwordErrorId = accessibilityUtils.generateId('password-error');
  const confirmPasswordErrorId = accessibilityUtils.generateId(
    'confirmPassword-error'
  );
  const passwordStrengthId = accessibilityUtils.generateId('password-strength');
  const termsErrorId = accessibilityUtils.generateId('terms-error');
  const privacyErrorId = accessibilityUtils.generateId('privacy-error');

  /**
   * Gets the aria-describedby value for the password field
   */
  const getPasswordAriaDescribedBy = () => {
    const hasError = !!errors.password;
    const hasPassword = !!formData.password;

    if (hasError && hasPassword) {
      return `${passwordErrorId} ${passwordStrengthId}`;
    }
    if (hasError) {
      return passwordErrorId;
    }
    if (hasPassword) {
      return passwordStrengthId;
    }
    return undefined;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: newValue,
    }));

    // Use validation hook's input change handler
    const updatedFormData = {
      ...formData,
      [name]: newValue,
    };
    handleValidationChange(name as keyof FormData, newValue, updatedFormData);

    // Update field validation states for visual feedback
    if (
      name === 'email' ||
      name === 'firstName' ||
      name === 'lastName' ||
      name === 'phoneNumber' ||
      name === 'password' ||
      name === 'confirmPassword'
    ) {
      const fieldName = name as keyof typeof fieldValidationStates;
      const hasError = errors[fieldName];
      const hasValue =
        typeof newValue === 'string' && newValue.trim().length > 0;

      let validationState: 'idle' | 'valid' | 'invalid' = 'idle';
      if (hasError) {
        validationState = 'invalid';
      } else if (hasValue) {
        validationState = 'valid';
      }

      setFieldValidationStates(prev => ({
        ...prev,
        [fieldName]: validationState,
      }));
    }
  };

  /**
   * Gets the validation icon for a field based on its state.
   */
  const getValidationIcon = (fieldName: keyof typeof fieldValidationStates) => {
    const state = fieldValidationStates[fieldName];
    const hasError = !!errors[fieldName];

    if (state === 'idle') return null;

    if (hasError) {
      return (
        <Zoom in>
          <Error color="error" sx={{ fontSize: 20 }} />
        </Zoom>
      );
    }

    if (state === 'valid') {
      return (
        <Zoom in>
          <CheckCircle color="success" sx={{ fontSize: 20 }} />
        </Zoom>
      );
    }

    return null;
  };

  /**
   * Handle resend verification email request
   */
  const handleResendVerification = async () => {
    if (!turnstileToken) {
      setApiError(t('register.errors.verificationRequired'));
      return;
    }

    setIsResending(true);
    setApiError('');

    try {
      await emailVerificationRequest({
        email: unverifiedEmail,
        turnstile_token: turnstileToken,
        language: i18n.language,
      }).unwrap();

      // Show success message and navigate to verification page
      setResendSuccess(true);
      setShowResendVerificationButton(false);

      // Navigate to email verification page after short delay
      setTimeout(() => {
        navigate('/verify-email', {
          state: {
            email: unverifiedEmail,
          },
        });
      }, 1500);
    } catch (error: any) {
      setApiError(
        error.data?.detail ||
          'Failed to resend verification email. Please try again.'
      );
    } finally {
      setIsResending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');
    setIsSubmitting(true);

    if (!validateForm(formData)) {
      setIsSubmitting(false);
      return;
    }

    if (!turnstileToken) {
      setApiError(t('register.errors.verificationRequired'));
      setIsSubmitting(false);
      return;
    }

    try {
      // Get selected plan data if available
      const planData = selectedPlan || {
        selectedPlan: 'free',
        billingCycle: 'monthly',
      };

      // Register user with basic data (plan assignment handled post-registration)
      await register({
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone_number: formData.phoneNumber || undefined,
        password: formData.password,
        password_confirm: formData.confirmPassword,
        terms_accepted: formData.termsAccepted,
        privacy_accepted: formData.privacyAccepted,
        marketing_emails: formData.marketingEmails,
        turnstile_token: turnstileToken,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: i18n.language, // Send current i18next language (en/es/fr)
      }).unwrap();

      // Clear plan selection from sessionStorage after successful registration
      sessionStorage.removeItem('selectedPlan');

      // TODO: Post-registration plan assignment
      // This will be implemented in Phase 2B when backend supports plan assignment
      // For now, plan assignment should be handled by the backend during user creation
      console.log('Registration successful with plan selection:', planData);

      // Call custom success handler if provided, otherwise navigate to email verification
      if (onRegistrationSuccess) {
        onRegistrationSuccess();
      } else {
        // Navigate with plan context for potential trial activation messaging
        navigate('/verify-email', {
          state: {
            email: formData.email,
            selectedPlan: planData.selectedPlan,
            billingCycle: planData.billingCycle,
          },
        });
      }
    } catch (error: any) {
      // Reset enhanced error state
      setShowResendVerificationButton(false);
      setShowForgotPasswordLink(false);
      setResendSuccess(false);

      if (error.data?.non_field_errors) {
        // Handle Django's non_field_errors (like password validation)
        setApiError(error.data.non_field_errors[0]);
      } else if (error.data?.detail) {
        setApiError(error.data.detail);
      } else if (error.data?.email) {
        // Enhanced email error handling with verification status check
        const emailError = error.data.email[0];

        // Check for enhanced metadata from backend
        if (error.data.email_exists) {
          // Backend returns metadata as arrays when raised in ValidationError
          // Extract the actual values from arrays
          const emailVerifiedValue = Array.isArray(error.data.email_verified)
            ? error.data.email_verified[0]
            : error.data.email_verified;
          const userEmailValue = Array.isArray(error.data.user_email)
            ? error.data.user_email[0]
            : error.data.user_email;

          // Convert string 'True'/'False' to boolean
          const isVerified =
            emailVerifiedValue === true ||
            emailVerifiedValue === 'True' ||
            emailVerifiedValue === 'true';

          if (isVerified) {
            // Email exists and is verified - show login/forgot password options
            setApiError(
              `${emailError} This account is already verified. Try logging in or resetting your password.`
            );
            setShowForgotPasswordLink(true);
          } else {
            // Email exists but not verified - offer to resend verification
            setApiError(
              `${emailError} This email is not yet verified. Would you like us to resend the verification email?`
            );
            setShowResendVerificationButton(true);
            setUnverifiedEmail(userEmailValue || formData.email);
          }
        } else {
          // Standard email error (not enhanced)
          setApiError(emailError);
        }
      } else if (error.data?.password) {
        // Handle password-specific field errors
        setApiError(error.data.password[0]);
      } else {
        setApiError(t('register.errors.registrationFailed'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Loading Progress Bar */}
      {(isLoading || isSubmitting) && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            borderRadius: '4px 4px 0 0',
          }}
        />
      )}

      {/* Loading Backdrop */}
      <Backdrop
        sx={{
          position: 'absolute',
          zIndex: 1,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          borderRadius: 1,
        }}
        open={isSubmitting}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            {t('register.loading.creatingAccount')}
          </Typography>
        </Box>
      </Backdrop>

      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        role={ARIA_ROLES.FORM}
        aria-label={t('register.accessibility.formLabel')}
        id={formId}
      >
        {/* Tutorial Video Button */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: 'action.hover',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('register.tutorial.description', {
              defaultValue:
                'New to tools.dance? Watch our quick tutorial to learn how to create your free account.',
            })}
          </Typography>
          <YouTubeVideoButton
            videoId="2Z2AzDZzw0U"
            videoTitle="Learn how to create your free tools.dance account"
            buttonText="Watch Tutorial"
            variant="outlined"
            size="small"
          />
        </Box>

        <Fade in={!!apiError || resendSuccess}>
          <Box>
            {apiError && (
              <Alert
                severity="error"
                data-testid="error-message"
                sx={{
                  mb: 2,
                  '& .MuiAlert-icon': {
                    fontSize: '1.5rem',
                  },
                }}
              >
                {apiError}
              </Alert>
            )}
            {resendSuccess && (
              <Alert
                severity="success"
                sx={{
                  mb: 2,
                  '& .MuiAlert-icon': {
                    fontSize: '1.5rem',
                  },
                }}
              >
                Verification email sent successfully! Redirecting...
              </Alert>
            )}
          </Box>
        </Fade>

        {/* Resend Verification Button - for unverified emails */}
        {showResendVerificationButton && (
          <Fade in>
            <Box sx={{ mb: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleResendVerification}
                disabled={isResending || !turnstileToken}
                sx={{
                  borderWidth: '2px',
                  '&:hover': {
                    borderWidth: '2px',
                  },
                }}
              >
                {isResending ? (
                  <CircularProgress size={24} />
                ) : (
                  'Resend Verification Email'
                )}
              </Button>
            </Box>
          </Fade>
        )}

        {/* Login/Forgot Password Links - for verified emails */}
        {showForgotPasswordLink && (
          <Fade in>
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                gap: 2,
                justifyContent: 'center',
              }}
            >
              <Button
                component={Link}
                href="/login"
                variant="outlined"
                sx={{
                  flex: 1,
                  borderWidth: '2px',
                  '&:hover': {
                    borderWidth: '2px',
                  },
                }}
              >
                Sign In
              </Button>
              <Button
                component={Link}
                href="/forgot-password"
                variant="outlined"
                sx={{
                  flex: 1,
                  borderWidth: '2px',
                  '&:hover': {
                    borderWidth: '2px',
                  },
                }}
              >
                Forgot Password?
              </Button>
            </Box>
          </Fade>
        )}

        {/* Selected Plan Display */}
        {selectedPlan && (
          <Box
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Star sx={{ fontSize: 24 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                {getPlanDisplayName(selectedPlan.selectedPlan)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {t('register.plan.registrationMessage', {
                    cycle: (() => {
                      if (selectedPlan.selectedPlan === 'free') {
                        return t('register.plan.cycle.free');
                      }
                      if (selectedPlan.billingCycle === 'yearly') {
                        return t('register.plan.cycle.yearly');
                      }
                      return t('register.plan.cycle.monthly');
                    })(),
                  })}
                </Typography>
                {getPlanBadge(selectedPlan.selectedPlan) && (
                  <Chip
                    label={getPlanBadge(selectedPlan.selectedPlan)}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'inherit',
                      fontWeight: 600,
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            label={t('register.form.label.firstName')}
            name="firstName"
            data-testid="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            error={!!errors.firstName}
            helperText={errors.firstName}
            disabled={isLoading || isSubmitting}
            required
            aria-required
            aria-invalid={!!errors.firstName}
            aria-describedby={errors.firstName ? firstNameErrorId : undefined}
            FormHelperTextProps={{
              id: errors.firstName ? firstNameErrorId : undefined,
            }}
            InputProps={{
              endAdornment: getValidationIcon('firstName'),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: '2px',
                },
                '&.Mui-focused fieldset': {
                  borderWidth: '2px',
                },
                transition: 'all 0.2s ease-in-out',
              },
            }}
          />
          <TextField
            fullWidth
            label={t('register.form.label.lastName')}
            name="lastName"
            data-testid="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            error={!!errors.lastName}
            helperText={errors.lastName}
            disabled={isLoading || isSubmitting}
            required
            aria-required
            aria-invalid={!!errors.lastName}
            aria-describedby={errors.lastName ? lastNameErrorId : undefined}
            FormHelperTextProps={{
              id: errors.lastName ? lastNameErrorId : undefined,
            }}
            InputProps={{
              endAdornment: getValidationIcon('lastName'),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: '2px',
                },
                '&.Mui-focused fieldset': {
                  borderWidth: '2px',
                },
                transition: 'all 0.2s ease-in-out',
              },
            }}
          />
        </Box>

        <TextField
          fullWidth
          label={t('register.form.label.email')}
          name="email"
          data-testid="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          error={!!errors.email}
          helperText={
            errors.email ||
            (emailDisabled ? t('register.form.hint.emailFromInvitation') : '')
          }
          disabled={emailDisabled || isLoading || isSubmitting}
          required
          aria-required
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? emailErrorId : undefined}
          FormHelperTextProps={{
            id: errors.email ? emailErrorId : undefined,
          }}
          InputProps={{
            endAdornment: getValidationIcon('email'),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'primary.main',
                borderWidth: '2px',
              },
              '&.Mui-focused fieldset': {
                borderWidth: '2px',
              },
              transition: 'all 0.2s ease-in-out',
              ...(emailDisabled && {
                '& fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: '1px',
                },
                backgroundColor: 'action.hover',
              }),
            },
            ...(emailDisabled && {
              '& .MuiInputBase-input': {
                color: 'text.primary',
                fontWeight: 'medium',
              },
            }),
          }}
        />

        <TextField
          fullWidth
          label={t('register.form.label.phoneNumber')}
          name="phoneNumber"
          data-testid="phoneNumber"
          value={formData.phoneNumber}
          onChange={handleInputChange}
          error={!!errors.phoneNumber}
          helperText={errors.phoneNumber}
          disabled={isLoading || isSubmitting}
          aria-invalid={!!errors.phoneNumber}
          aria-describedby={errors.phoneNumber ? phoneErrorId : undefined}
          FormHelperTextProps={{
            id: errors.phoneNumber ? phoneErrorId : undefined,
          }}
          InputProps={{
            endAdornment: getValidationIcon('phoneNumber'),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'primary.main',
                borderWidth: '2px',
              },
              '&.Mui-focused fieldset': {
                borderWidth: '2px',
              },
              transition: 'all 0.2s ease-in-out',
            },
          }}
        />

        <TextField
          fullWidth
          label={t('register.form.label.password')}
          name="password"
          data-testid="password"
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleInputChange}
          error={!!errors.password}
          helperText={errors.password}
          disabled={isLoading || isSubmitting}
          required
          aria-required
          aria-invalid={!!errors.password}
          aria-describedby={getPasswordAriaDescribedBy()}
          FormHelperTextProps={{
            id: errors.password ? passwordErrorId : undefined,
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {getValidationIcon('password')}
                <IconButton
                  aria-label={t('register.accessibility.passwordToggle')}
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  disabled={isLoading || isSubmitting}
                  sx={{ ml: 0.5 }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'primary.main',
                borderWidth: '2px',
              },
              '&.Mui-focused fieldset': {
                borderWidth: '2px',
              },
              transition: 'all 0.2s ease-in-out',
            },
          }}
        />

        {formData.password && (
          <Box sx={{ mt: 1, mb: 2 }} id={passwordStrengthId}>
            <Typography variant="caption" color="text.secondary">
              {t('register.passwordStrength.label')}{' '}
              {getPasswordStrengthLabel(
                getPasswordStrengthScore(formData.password)
              )}
            </Typography>
            <Box
              sx={{
                width: '100%',
                height: 4,
                backgroundColor: '#e0e0e0',
                borderRadius: 2,
                mt: 0.5,
              }}
            >
              <Box
                sx={{
                  width: `${(getPasswordStrengthScore(formData.password) / 4) * 100}%`,
                  height: '100%',
                  backgroundColor: getPasswordStrengthColor(
                    getPasswordStrengthScore(formData.password)
                  ),
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                }}
              />
            </Box>
            {validatePasswordStrength(formData.password).length > 0 && (
              <Box sx={{ mt: 1 }}>
                {validatePasswordStrength(formData.password).map(error => (
                  <Typography
                    key={error}
                    variant="caption"
                    color="error"
                    display="block"
                  >
                    • {error}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )}

        <TextField
          fullWidth
          label={t('register.form.label.confirmPassword')}
          name="confirmPassword"
          data-testid="confirmPassword"
          type={showConfirmPassword ? 'text' : 'password'}
          value={formData.confirmPassword}
          onChange={handleInputChange}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
          disabled={isLoading || isSubmitting}
          required
          aria-required
          aria-invalid={!!errors.confirmPassword}
          aria-describedby={
            errors.confirmPassword ? confirmPasswordErrorId : undefined
          }
          FormHelperTextProps={{
            id: errors.confirmPassword ? confirmPasswordErrorId : undefined,
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {getValidationIcon('confirmPassword')}
                <IconButton
                  aria-label={t('register.accessibility.confirmPasswordToggle')}
                  onClick={handleClickShowConfirmPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  disabled={isLoading || isSubmitting}
                  sx={{ ml: 0.5 }}
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'primary.main',
                borderWidth: '2px',
              },
              '&.Mui-focused fieldset': {
                borderWidth: '2px',
              },
              transition: 'all 0.2s ease-in-out',
            },
          }}
        />

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                name="termsAccepted"
                checked={formData.termsAccepted}
                onChange={handleInputChange}
                disabled={isLoading || isSubmitting}
                aria-describedby={
                  errors.termsAccepted ? termsErrorId : undefined
                }
                aria-invalid={!!errors.termsAccepted}
              />
            }
            label={
              <Typography variant="body2">
                {t('register.terms.accept')}{' '}
                <Link href="/terms" target="_blank">
                  {t('register.terms.termsOfService')}
                </Link>
                {t('register.terms.required')}
              </Typography>
            }
          />
          {errors.termsAccepted && (
            <Typography variant="caption" color="error" id={termsErrorId}>
              {errors.termsAccepted}
            </Typography>
          )}
        </Box>

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                name="privacyAccepted"
                checked={formData.privacyAccepted}
                onChange={handleInputChange}
                disabled={isLoading}
                aria-describedby={
                  errors.privacyAccepted ? privacyErrorId : undefined
                }
                aria-invalid={!!errors.privacyAccepted}
              />
            }
            label={
              <Typography variant="body2">
                {t('register.terms.accept')}{' '}
                <Link href="/privacy" target="_blank">
                  {t('register.terms.privacyPolicy')}
                </Link>
                {t('register.terms.required')}
              </Typography>
            }
          />
          {errors.privacyAccepted && (
            <Typography variant="caption" color="error" id={privacyErrorId}>
              {errors.privacyAccepted}
            </Typography>
          )}
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              name="marketingEmails"
              checked={formData.marketingEmails}
              onChange={handleInputChange}
              disabled={isLoading}
            />
          }
          label={
            <Typography variant="body2">
              {t('register.marketing.label')}
            </Typography>
          }
          sx={{ mb: 2 }}
        />

        <TurnstileWidget
          onSuccess={setTurnstileToken}
          onError={() => setApiError(t('register.errors.verificationFailed'))}
          onExpire={() => setTurnstileToken('')}
          disabled={isLoading}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={isLoading || !turnstileToken}
          data-testid="register-submit"
          sx={{ mt: 3, mb: 2 }}
        >
          {isLoading ? (
            <CircularProgress size={24} />
          ) : (
            t('register.form.submit')
          )}
        </Button>

        <Typography variant="body2" align="center">
          {t('register.links.haveAccount')}{' '}
          <Link href="/login" underline="hover">
            {t('register.links.signIn')}
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default RegisterForm;
